import { useContext, useState, useEffect, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { noop } from 'lodash-es';

import { Alert, Badge, Breadcrumbs, Icon, Text, tokens } from '@hotjar/hotsauce';
import { useNavigation } from '@hotjar/navigation/src/hooks/useNavigation';
import type { Site } from '@hotjar/site';
import { useSites, useCurrentSiteId } from '@hotjar/site';
import { ErrorBoundary } from '@hotjar/error-handler';

import { LegacyContext } from './context/global-context';
import type { Integration } from './types';
import { Resources } from './components/Resources';
import { Carousel } from './components/Carousel';
import { YoutubeVideo } from './components/YoutubeVideo';
import { IntegrationActions } from './components/IntegrationActions';
import { useIntegrations } from './hooks/use-integrations';
import { IntegrationSiteDropdown } from './components/IntegrationSiteDropdown';
import { integrationGatesOpen } from './utils';

const ContainerWithBackground = styled.div`
  background-color: ${tokens.color.bg_info};
  padding: ${tokens.spacer.x3} ${tokens.spacer.x4pt5};
`;

const ContentContainer = styled.section<{ noVideo: boolean }>`
  display: flex;
  padding: ${tokens.spacer.x5} 0 ${tokens.spacer.x4};
  max-width: ${({ noVideo }) => (noVideo ? '600' : '800')}px;
  margin: 0 auto;
`;

const DropdownContainer = styled(ContentContainer)`
  display: flex;
  flex-direction: column;
  padding: ${tokens.spacer.x5} 0 0;
  max-width: ${({ noVideo }) => (noVideo ? '600' : '800')}px;
  margin: 0 auto;
`;

const AlertContainer = styled.div<{ noVideo: boolean }>`
  padding: 0 0 ${tokens.spacer.x5};
  max-width: ${({ noVideo }) => (noVideo ? '600' : '800')}px;
  margin: 0 auto;
`;

const ContentBox = styled.div<{ fullWidth: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${({ fullWidth }) => (fullWidth ? '100%' : '400px')};
  padding-right: ${tokens.spacer.x2};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: ${tokens.spacer.x1};
  gap: ${tokens.spacer.x1};
`;

const Name = styled(Text)`
  margin-bottom: 0;
`;

const Provider = styled(Text)`
  padding-top: ${tokens.spacer.x0pt5}; // to align with bottom line of the Name
`;

const Content = styled.div`
  width: 600px;
  margin: 0 auto;
  padding: ${tokens.spacer.x6} 0;
  position: relative;
`;

const SectionTitle = styled(Text)`
  margin-bottom: ${tokens.spacer.x1pt5};
`;

const ResourcesTitle = styled(SectionTitle)`
  margin-bottom: ${tokens.spacer.x1pt5};
  margin-top: ${tokens.spacer.x4};
`;

const DropdownTitle = styled(Text)`
  margin-bottom: ${tokens.spacer.x1};
`;

const Bold = styled.span`
  font-weight: bold;
`;

const NotAvailableInfo = styled(Text)`
  margin-bottom: ${tokens.spacer.x1};
`;

const CustomIcon = styled(Icon)`
  width: 24px;
  height: 24px;
`;

const SelectBox = styled.div`
  width: 320px;
  margin-bottom: ${tokens.spacer.x1};
`;

export const IntegrationDetailsPage = (): JSX.Element | null => {
  const { buildHrefFromRoute } = useNavigation();
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const { integrationName, siteId: siteIdFromParams } = useParams();
  const { t } = useTranslation();
  const {
    routes = { integrations: '', integrationDetails: '' },
    useAuthorization = () => ({ hasPermission: () => false }),
    usePageTitle = () => null,
  } = useContext(LegacyContext);
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | undefined>(
    undefined,
  );
  const {
    query: { data: sites = [] },
  } = useSites();
  const { hasPermission } = useAuthorization();
  const currentSiteId = useCurrentSiteId();
  const navigate = useNavigate();

  const siteId = siteIdFromParams ?? currentSiteId;
  const { integrations, externalIntegrations } = useIntegrations(Number(siteId));
  const isAdmin = hasPermission('site', siteId, 'site-integrations-update') ?? false;
  const canUse = integrationGatesOpen(selectedIntegration ?? null);

  usePageTitle(`${t('integrations:page.title')} - Hotjar`);

  const storeNewSite = useCallback(
    (newSiteId: string, callback = () => noop) => {
      const newSite = sites.find((site) => site.id === Number(newSiteId));

      if (newSite) {
        setSelectedSite(newSite);
        callback();
      }
    },
    [sites],
  );

  const handleSiteChange = useCallback(
    (id: string) => {
      if (integrationName) {
        const cb = () =>
          navigate(buildHrefFromRoute(routes.integrationDetails, { siteId: id, integrationName }));

        storeNewSite(id, cb);
      }
    },
    [buildHrefFromRoute, navigate, routes.integrationDetails, integrationName, storeNewSite],
  );

  useEffect(() => {
    if (integrationName && siteId) {
      const integration =
        integrations.find((integrationData) => integrationData.slug === integrationName) ||
        externalIntegrations.find((integrationData) => integrationData.slug === integrationName);

      setSelectedIntegration(integration);
      storeNewSite(siteId);
    }
  }, [integrationName, storeNewSite, siteId, integrations, externalIntegrations]);

  if (!integrationName || !selectedIntegration || !selectedSite) {
    return null;
  }

  return (
    <ErrorBoundary squadSentryTag="integrations">
      <article>
        <ContainerWithBackground>
          <Breadcrumbs>
            <Breadcrumbs.Item href={buildHrefFromRoute(routes.integrations)}>
              {t('integrations:details.header.breadcrumb-parent')}
            </Breadcrumbs.Item>
            <Breadcrumbs.Item isCurrent>{`${t(selectedIntegration.name)} ${t(
              'integrations:details.header.breadcrumb-suffix',
            )}`}</Breadcrumbs.Item>
          </Breadcrumbs>
          <DropdownContainer noVideo={selectedIntegration.videoUrl === null}>
            <DropdownTitle as="p" textType="subtitle2">
              {t('integrations:page.dropdown-title')}
            </DropdownTitle>
            <SelectBox>
              {sites.length > 0 && (
                <IntegrationSiteDropdown
                  value={selectedSite?.id ?? 0}
                  onChange={handleSiteChange}
                  sites={sites}
                />
              )}
            </SelectBox>
          </DropdownContainer>
          <ContentContainer noVideo={selectedIntegration.videoUrl === null}>
            <ContentBox fullWidth={selectedIntegration.videoUrl === null}>
              <Header>
                <CustomIcon icon={selectedIntegration.icon} />
                <Name as="h4" textType="header4">
                  {t(selectedIntegration.name)}
                </Name>
                {selectedIntegration.isBeta && <Badge kind="beta" size="s" />}
                <Provider textType="body1" textColor="text_disabled">
                  {`${t('integrations:card.by').toLowerCase()} ${t(selectedIntegration.provider)}`}
                </Provider>
              </Header>
              {!canUse && (
                <NotAvailableInfo textType="caption" textColor="text_disabled">
                  {selectedIntegration.tier && (
                    <Trans
                      i18nKey={`integrations:gate-closed.${selectedIntegration.tier}`}
                      components={[<Bold />]}
                    />
                  )}
                </NotAvailableInfo>
              )}
              <Text textType="body1">{t(selectedIntegration.description)}</Text>
              <div aria-label={t('integrations:page.actions')}>
                <IntegrationActions
                  canUse={canUse}
                  integration={selectedIntegration}
                  isAdmin={isAdmin}
                  siteId={siteId}
                  siteName={selectedSite.name}
                />
              </div>
            </ContentBox>
            <YoutubeVideo videoUrl={selectedIntegration.videoUrl} />
          </ContentContainer>
          {!isAdmin && canUse && !selectedIntegration.isAlwaysOn ? (
            <AlertContainer noVideo={selectedIntegration.videoUrl === null}>
              <Alert
                actionText={t('integrations:details.header.not-admin-link-label')}
                actionHref={buildHrefFromRoute(routes.teamList)}
              >
                {selectedIntegration.isEnabled
                  ? t('integrations:details.header.not-admin-alert-copy-disable')
                  : t('integrations:details.header.not-admin-alert-copy-enable')}
              </Alert>
            </AlertContainer>
          ) : null}
        </ContainerWithBackground>
        <Content>
          {selectedIntegration.descriptionLarge && (
            <>
              <SectionTitle as="h4" textType="header4">
                {t('integrations:details.description')}
              </SectionTitle>
              <Text as="p" textType="body1">
                {t(selectedIntegration.descriptionLarge)}
              </Text>
            </>
          )}
          {selectedIntegration.noticeDescription && (
            <Alert kind="warning" title={t(selectedIntegration.noticeTitle || '')}>
              {t(selectedIntegration.noticeDescription)}
            </Alert>
          )}
          {selectedIntegration.resources.length > 0 ? (
            <>
              <ResourcesTitle as="h4" textType="header4">
                {t('integrations:details.resources')}
              </ResourcesTitle>
              <Resources resources={selectedIntegration.resources} />
              {selectedIntegration.images && selectedIntegration.images.length > 0 && (
                <Carousel imagesUrls={selectedIntegration.images} />
              )}
            </>
          ) : null}
        </Content>
      </article>
    </ErrorBoundary>
  );
};
